import { useState, useEffect } from 'react';
import { Settings, CheckCircle, Plus, Trash2, Tags } from 'lucide-react';
import usePotvrzeni from '../ui/usePotvrzeni';
import { SECTION_COLOR } from '../../pages/smeny/spolecne';

/**
 * Směny → ⚙ Nastavení směn → Role (barva a ikona v plánu, vazba na kariéru).
 *
 * Přestěhováno beze změny chování ze starého Nastavení → Zaměstnanci → Role
 * (revize Nastavení, 2026-10-12): role patří k plánování směn, ne do
 * společného Nastavení. Potvrzení smazání jde přes usePotvrzeni.
 */
const ROLE_BARVY = ['#6B7280', '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#0CC0DF', '#F97316', '#14B8A6'];
const ROLE_IKONY = ['👤', '🍕', '🚗', '📦', '🧹', '📋', '🎓', '🔧', '☎️', '💰', '🏠', '⭐', '🎯', '🛒', '🍳', '🥤'];

export default function RoleSprava({ isDark }) {
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const [role, setRole] = useState([]);
    const [kariery, setKariery] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ nazev: '', kod: '', barva: '#6B7280', ikona: '👤', popis: '', kariera_id: '' });
    const [saving, setSaving] = useState(false);

    const cardStyle = isDark
        ? { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10 }
        : { background: 'var(--surface-base)', border: '1px solid var(--border-default)', borderRadius: 10 };
    const inputStyle = {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        borderRadius: 6, padding: '6px 10px', fontSize: 13,
        color: 'var(--text-primary)', outline: 'none', width: '100%',
    };

    const load = () => {
        fetch('/api/role').then(r => r.json()).then(setRole);
        fetch('/api/ciselniky/kariery').then(r => r.json()).then(setKariery);
    };
    useEffect(() => { load(); }, []);

    const handleSave = async () => {
        if (!form.nazev.trim() || !form.kod.trim()) return;
        setSaving(true);
        const body = { ...form, kariera_id: form.kariera_id || null };
        const method = editing === 'new' ? 'POST' : 'PUT';
        const url = editing === 'new' ? '/api/role' : `/api/role/${editing}`;
        const res = await fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
            body: JSON.stringify(body),
        });
        if (res.ok) { load(); setEditing(null); }
        setSaving(false);
    };

    const handleDelete = async (id) => {
        if (!(await potvrdit('Smazat tuto roli?', { text: 'Směny, které ji mají, ji ztratí.', potvrdit: 'Smazat' }))) return;
        await fetch(`/api/role/${id}`, { method: 'DELETE', headers: { 'X-CSRF-TOKEN': token } });
        load();
    };

    const toggleAktivni = async (r) => {
        await fetch(`/api/role/${r.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
            body: JSON.stringify({ aktivni: !r.aktivni }),
        });
        load();
    };

    const openEdit = (r) => {
        setEditing(r ? r.id : 'new');
        setForm(r ? { nazev: r.nazev, kod: r.kod, barva: r.barva || '#6B7280', ikona: r.ikona || '👤', popis: r.popis || '', kariera_id: r.kariera_id || '' }
                  : { nazev: '', kod: '', barva: '#6B7280', ikona: '👤', popis: '', kariera_id: '' });
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Role pro plánování směn a přiřazování aktivit</p>
                <button onClick={() => openEdit(null)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                    style={{ background: SECTION_COLOR, color: 'white' }}>
                    <Plus size={14} /> Nová role
                </button>
            </div>

            {editing !== null && (
                <div className="mb-4 p-3 md:p-4 rounded-xl" style={cardStyle}>
                    <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
                        <div>
                            <label className="text-[11px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Název</label>
                            <input value={form.nazev} onChange={e => setForm(f => ({ ...f, nazev: e.target.value }))}
                                placeholder="např. Řidič" style={inputStyle} />
                        </div>
                        <div>
                            <label className="text-[11px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Kód</label>
                            <input value={form.kod} onChange={e => setForm(f => ({ ...f, kod: e.target.value.toUpperCase().replace(/\s/g, '_') }))}
                                placeholder="např. RIDIC" style={inputStyle} />
                        </div>
                    </div>
                    <div className="grid gap-3 mt-3 grid-cols-1 md:grid-cols-2">
                        <div>
                            <label className="text-[11px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Kariéra</label>
                            <select value={form.kariera_id} onChange={e => setForm(f => ({ ...f, kariera_id: e.target.value }))}
                                style={inputStyle}>
                                <option value="">— bez kariéry —</option>
                                {kariery.map(k => (
                                    <option key={k.id} value={k.id}>{k.ikona} {k.nazev}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-[11px] font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Popis</label>
                            <input value={form.popis} onChange={e => setForm(f => ({ ...f, popis: e.target.value }))}
                                placeholder="Volitelný popis..." style={inputStyle} />
                        </div>
                    </div>
                    <div className="mt-3 flex flex-col md:flex-row gap-3 md:gap-4">
                        <div>
                            <label className="text-[11px] font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Barva</label>
                            <div className="flex gap-1.5 flex-wrap">
                                {ROLE_BARVY.map(b => (
                                    <button key={b} onClick={() => setForm(f => ({ ...f, barva: b }))}
                                        style={{
                                            width: 22, height: 22, borderRadius: 5, background: b,
                                            border: form.barva === b ? '2px solid white' : '2px solid transparent',
                                            boxShadow: form.barva === b ? `0 0 0 1px ${b}` : 'none',
                                        }} />
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="text-[11px] font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Ikona</label>
                            <div className="flex gap-1 flex-wrap">
                                {ROLE_IKONY.map(i => (
                                    <button key={i} onClick={() => setForm(f => ({ ...f, ikona: i }))}
                                        className="text-base"
                                        style={{
                                            width: 28, height: 28, borderRadius: 5, lineHeight: 1,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            background: form.ikona === i ? (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)') : 'transparent',
                                            border: form.ikona === i ? `1px solid ${SECTION_COLOR}` : '1px solid transparent',
                                        }}>
                                        {i}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                        <button onClick={handleSave} disabled={saving || !form.nazev.trim() || !form.kod.trim()}
                            className="px-4 py-1.5 rounded-lg text-xs font-medium"
                            style={{ background: SECTION_COLOR, color: 'white', opacity: saving ? 0.6 : 1 }}>
                            {saving ? 'Ukládám...' : (editing === 'new' ? 'Vytvořit' : 'Uložit')}
                        </button>
                        <button onClick={() => setEditing(null)}
                            className="px-4 py-1.5 rounded-lg text-xs"
                            style={{ color: 'var(--text-muted)', background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }}>
                            Zrušit
                        </button>
                    </div>
                </div>
            )}

            <div className="flex flex-col gap-2">
                {role.map(r => (
                    <div key={r.id} className="flex items-center gap-3 p-3 rounded-xl" style={cardStyle}>
                        <div style={{
                            width: 36, height: 36, borderRadius: 7, flexShrink: 0,
                            background: `${r.barva}20`, border: `1px solid ${r.barva}40`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                        }}>
                            {r.ikona || '👤'}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{r.nazev}</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded font-mono"
                                    style={{ background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', color: 'var(--text-muted)' }}>
                                    {r.kod}
                                </span>
                                {!r.aktivni && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded"
                                        style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444' }}>neaktivní</span>
                                )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                                {r.kariera && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1"
                                        style={{ background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', color: 'var(--text-secondary)' }}>
                                        {r.kariera.ikona} {r.kariera.nazev}
                                    </span>
                                )}
                                {r.popis && <span className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{r.popis}</span>}
                            </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => toggleAktivni(r)} title={r.aktivni ? 'Deaktivovat' : 'Aktivovat'}
                                className="p-1.5 rounded-lg" style={{ color: r.aktivni ? '#10B981' : 'var(--text-muted)' }}>
                                <CheckCircle size={14} />
                            </button>
                            <button onClick={() => openEdit(r)} className="p-1.5 rounded-lg" style={{ color: 'var(--text-muted)' }}>
                                <Settings size={14} />
                            </button>
                            <button onClick={() => handleDelete(r.id)} className="p-1.5 rounded-lg" style={{ color: '#EF4444' }}>
                                <Trash2 size={14} />
                            </button>
                        </div>
                    </div>
                ))}
                {role.length === 0 && (
                    <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
                        <Tags size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                        <p className="text-sm">Zatím nemáte žádné role</p>
                    </div>
                )}
            </div>
            {potvrzeni}
        </div>
    );
}
